import React, {useContext, useEffect, useState} from "react";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {Periodo} from "@commons/types";
import {ContatoriRichiesteWF} from "./comps/ContatoriRichiesteWF";
import {WeekCell} from "../comps/WeekCell";
import RiepilogoIC, {RiepilogoICCompParams} from "../comps/RiepilogoIC";
import RiepilogoGiust from "../comps/RiepilogoGiust";
import {DateTime} from "luxon";
import css from '../calendarioPresenze_WF.module.css'
import {DayCellDisclosure} from "../comps/DayCellDisclosure";
import {CalendarioChiusureRecord} from "@commons/mapping/CalendarioChiusureRecord";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda, selectUser} from "@commons/core.slice";
import {GestionePlus} from "@commons/services/presenze_calcolo/GestionePlus";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {TabellaWFRecord} from "@commons/mapping/TabellaWFRecord";
import UpdateOrReadWF from "./wf.employee.updateOrRead";
import UpdateOrInsertWF from "./wf.employee.updateOrInsert";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {mm2HHmmFormat} from "@commons/utils/util";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {usePresenzeCausali} from "@commons/services/presenze_api/workflow.queries";
import {TableRichiesteWF} from "../dashboardWF/TableRichiesteWF";
import DeleteWF from "./wf.employee.delete";
import {useQueryClient} from "@tanstack/react-query";
import {FPNetDate} from "@commons/types";
import classNames from "classnames";
import {IconLockFilled, IconPlus, IconX} from "@tabler/icons-react";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {TerminaleRecord} from "@commons/mapping/TerminaleRecord";
import {PresenzeContext} from "@commons/hooks/usePresenze";
import {isEditabile} from "@commons/utils/isEditabile";

interface CalendarioProps {
    lavoratore: LavoratorePresenzeRecord,
    periodo: Periodo,
    tabellaWF: TabellaWFRecord,
    calendarioChiusure: CalendarioChiusureRecord | null,
    onUpdateRichiesteWF: (richiesteWF: PresenzeCausaleWFRecord[]) => void,
    listaGiustificativi: GiustificativoRecord[],
    listaTerminali: TerminaleRecord[],
}

export const CalendarioWF: React.FC<CalendarioProps> = ({
                                                            lavoratore, periodo,
                                                            tabellaWF, onUpdateRichiesteWF, calendarioChiusure, listaGiustificativi,
                                                            listaTerminali,
                                                        }) => {
    const queryClient = useQueryClient();
    const azienda = useAppSelector(selectAzienda);
    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const {currentDay} = useContext(PresenzeContext);

    const riepilogoICParams: RiepilogoICCompParams = {
        idAziendaTabelle: lavoratore?.azienda?.idAziendaCDIC || azienda?.idAziendaCDIC,
        idAziendaMaster: lavoratore?.azienda?.idAziendaMaster || azienda?.idAziendaMaster,
        idDip: lavoratore.id,
        page: "WF",
        periodo,
    }

    const formattedPeriodo = DateTime
        .fromObject({year: periodo.anno, month: periodo.mese}, {locale: 'it'})
        .toFormat('LLLL yyyy');

    const [elencoGiuEvidenza, setNextElencoGiuEvidenza] = useState<string[]>([]);
    const isHighlighted = (codice: string) => {
        return !!elencoGiuEvidenza.find(c => c === codice);
    };
    const toggleGiustificativo = (codice: string) => {
        if (isHighlighted(codice))
            setNextElencoGiuEvidenza(elencoGiuEvidenza.filter(c => c !== codice));
        else
            setNextElencoGiuEvidenza(elencoGiuEvidenza.concat([codice]));
    }

    const [newWF, setNextNewWF] = useState<PresenzeCausaleWFRecord | null>(null);
    const [URI2Show, setNextURI2Show] = useState<string | null>(null);
    const [URI2Delete, setNextURI2Delete] = useState<string | null>(null);

    const loadResource = (uri: string) => {
        setNextURI2Show(uri);
    }
    const deleteResource = (uri: string) => {
        setNextURI2Delete(uri);
    }
    const onCancelEdit = () => {
        setNextNewWF(null);
        setNextURI2Show(null);
        setNextURI2Delete(null);
    }

    const isEmployee = user?.userClass === 'EMPLOYEE';

    const [currentWeek, setNextCurrentWeek] = useState<number | null>(null);

    const dataChiusura: DateTime | null = calendarioChiusure ? CalendarioChiusureRecord.getScadenza(calendarioChiusure, periodo.mese) : null;
    const isPeriodoChiuso = (azienda && user) ? GestionePlus.statoPeriodo(azienda, periodo, lavoratore?.days || null, null, user).chiuso : true;

    /**
     * Verifica se le richieste della giornata corrente sono modificabili!
     */
    const isWFEditabile = function () {
        // console.log(Periodo.getDate(periodo).toISO());
        return !isPeriodoChiuso && isEditabile(isEmployee, calendarioChiusure, Periodo.getDate(periodo).toISO(), periodo);
    };

    const minDate = tabellaWF && tabellaWF.decorrenza ? tabellaWF.decorrenza : DateTime.now().minus({years: 1}).toFormat('yyyy-LL-dd');
    const maxDate = tabellaWF && tabellaWF.fineValidita ? tabellaWF.fineValidita : DateTime.now().plus({years: 1}).toFormat('yyyy-LL-dd');

    const {richiesteWF, refetchRichiesteWF} = usePresenzeCausali(lavoratore, periodo, minDate, maxDate);

    // A salvataggio effettuato, allineo risorsa con esito del salvataggio e aggiorno l'elenco delle richieste (refetch di usePresenzeCausaliWF)
    const onSaveSuccess = (richiestaWF: PresenzeCausaleWFRecord) => {
        if (newWF != null)
            setNextNewWF({...newWF, ...richiestaWF});
        queryClient.invalidateQueries({queryKey: ["presenzeCausaliWF"]}); //???????????????
    }

    // effetto per aggiornare lavoratore.causaliWF con quelle appena caricate in <richiesteWF.data>, vedi workflow.tsx onUpdateRichiesteWF
    useEffect(() => {
        if (richiesteWF && richiesteWF.data && onUpdateRichiesteWF)
            onUpdateRichiesteWF(richiesteWF.data);

    }, [onUpdateRichiesteWF, richiesteWF]);


    const nuovaRichiesta = (day: PresenzeGiornataPlusRecord) => {
        const resource = PresenzeCausaleWFRecord.getFilledInstance(lavoratore, day, user!);
        setNextNewWF(resource);
    }

    const checkAccumuli = lavoratore.totaliMese && lavoratore.totaliMese.accumuliCHK && lavoratore.totaliMese.accumuliCHK.length > 0;

    return <div className={`${css.containerCalendario}`}>

        {/*INTESTAZIONE CALENDARIO*/}
        <div className={css.headerCalendarioWF}>

            {/* INDICAZIONE MESE */}
            <div className="col-span-1">
                <div className="flex flex-col w-fit">
                    <div className={classNames(css.periodo, 'md:mr-auto')}>
                        <span className="capitalize whitespace-nowrap">{formattedPeriodo}</span>
                        {isPeriodoChiuso && <>&nbsp;<IconLockFilled className="rounded bg-white text-[#235f6a] w-[24px] p-0.5"/></>}
                    </div>
                    {(!isPeriodoChiuso && dataChiusura) && <span className="text-xs">editabile fino al {dataChiusura.toFormat('dd/LL/yyyy')}</span>}
                </div>
            </div>

            {/* NOMINATIVO */}
            <div className="col-span-1">
                <div className="flex flex-col text-center w-fit mx-auto">
                    <span className="text-base sm:text-lg lg:text-xl 2xl:text-2xl">{lavoratore?.cognome}&nbsp;{lavoratore?.nome}</span>
                    <p className="text-xs text-center select-text">#{lavoratore?.id}</p>
                </div>
            </div>

            {/* CONTATORI */}
            <ContatoriRichiesteWF contatoriClassName="mx-auto md:mr-0 col-span-2 md:col-span-1" causaliWF={lavoratore.causaliWF} page='WF'/>
        </div>

        {/*CALENDARIO MENSILE*/}
        <div className={`${css.textColorCalendario} p-0.5 text-xs`}>
            {lavoratore.weeks?.map((week, weekIndex) => {
                const weekOpen = week.numWeekKey === currentWeek;

                return <div className="flex flex-row w-auto gap-x-0.5" key={weekIndex}>
                    {/* SETTIMANA */}
                    <WeekCell page="WF" week={week} lavoratore={lavoratore} tabellaWF={tabellaWF}/>
                    {/* GIORNI */}
                    <div className="w-full">{/*space-y-0.5*/}
                        {/* Horizontal lines - giorni settimana */}
                        {lavoratore.days && [0, 1, 2, 3, 4, 5, 6].map(dayIndex => {
                            const day = lavoratore.days![dayIndex + (weekIndex * 7)];
                            if (day) {
                                const previstoInMinuti = RiepilogoOrePlus.getTotaleHPrev(day.totali);
                                const hhPreviste = mm2HHmmFormat(previstoInMinuti);

                                return <div key={dayIndex} className="mb-0.5">
                                    <DayCellDisclosure page="WF" key={dayIndex} day={day} periodo={periodo} tabellaWF={tabellaWF} lavoratore={lavoratore}
                                                       causaliWF={lavoratore.causaliWF}
                                                       submitButtonText={<><IconX className="h-5 w-5"/> Chiudi</>}
                                                       elencoGiuEvidenza={elencoGiuEvidenza} hhPreviste={hhPreviste}
                                                       listaGiustificativi={listaGiustificativi} listaTerminali={listaTerminali}>

                                        <div className="flex justify-end mt-5 mb-5">
                                            {isWFEditabile() &&
                                                <button type="button" onClick={() => nuovaRichiesta(day)}
                                                        className="bg-blue-600 btnPrimary">
                                                    <IconPlus className="w-5 h-5"/> Richiesta
                                                </button>}
                                        </div>

                                        <TableRichiesteWF data={richiesteWF} loadResource={loadResource} deleteResource={deleteResource}
                                                          URI={URI2Show || URI2Delete}
                                                          currentDay={currentDay}
                                                          isEditabile={(rifDate: FPNetDate) => isWFEditabile()}
                                                          page="WF"/>

                                        {newWF &&
                                            <UpdateOrInsertWF data={newWF}
                                                                    periodo={periodo}
                                                                    onSaveSuccess={onSaveSuccess}
                                                                    onCancelEdit={onCancelEdit}
                                                                    hhPreviste={previstoInMinuti}
                                                                    hhWeekMedie={0}
                                                                    lavoratore={lavoratore}
                                                                    elencoGiustificativi={listaGiustificativi}
                                                                    richiesteWF={richiesteWF?.data}
                                                                    day={day}
                                                                    ope="UPDATE_OR_INSERT"
                                                                    isEditabile={(rifDate: FPNetDate) => isWFEditabile()}
                                        />}

                                        {URI2Show &&
                                            <UpdateOrReadWF URI={URI2Show}
                                                                     periodo={periodo}
                                                                     onSaveSuccess={onSaveSuccess}
                                                                     onCancelEdit={onCancelEdit}
                                                                     hhPreviste={previstoInMinuti}
                                                                     hhWeekMedie={0}
                                                                     lavoratore={lavoratore}
                                                                     elencoGiustificativi={listaGiustificativi}
                                                                     richiesteWF={richiesteWF?.data}
                                                                     day={day}
                                                                     isEditabile={(rifDate: FPNetDate) => false}//isWFEditabile()}
                                            />}

                                        {URI2Delete && <DeleteWF URI={URI2Delete}
                                                                 periodo={periodo}
                                                                 onSaveSuccess={onSaveSuccess}
                                                                 onCancelEdit={onCancelEdit}
                                                                 lavoratore={lavoratore}
                                                                 isEditabile={(rifDate: FPNetDate) => isWFEditabile()}
                                        />}

                                    </DayCellDisclosure>
                                </div>
                            } else return <div key={dayIndex} className="mb-0.5"></div>
                        })}
                    </div>
                </div>
            })}
        </div>

        {/*FOOTER CALENDARIO PER RIEPILOGHI*/}
        <div className={`${css.footerCalendario} border-t-4`}>
            <div className={`text-white bg-[#235F6A] ${css.intestazioneFooter}`}>
                {lavoratore.cognome}&nbsp;{lavoratore.nome}&nbsp;-&nbsp;<span className="capitalize">{formattedPeriodo}</span>
            </div>

            <div className={css.containerRiepiloghi}> {/* grid grid-cols-1 min-[880px]:grid-cols-2 */}
                {/* RIEPILOGOIC è lo stesso componente utilizzato nelle pagine MonitorWF, WF e Presenze - anche se passo solo page==='WF' - serve solo per cambiare lo stile se la pagina è MONITORWF */}
                {tabellaWF.showRiepilogoFR && <div>
                    <RiepilogoIC {...riepilogoICParams}/>
                </div>}

                <div className="row-start-2 min-[880px]:row-start-1 min-[880px]:col-start-2">
                    <RiepilogoGiust listaGiustificativi={listaGiustificativi} giustificativi={lavoratore.totaliMese?.giustificativi}
                                    isHighlighted={isHighlighted} toggleGiustificativo={toggleGiustificativo}/>
                </div>

                {/*<div>
                    {!isEmployee && checkAccumuli && <AccumuliDiControllo gestione={gestione}/>}
                </div>*/}
            </div>
        </div>
    </div>
}